import { toNumericRelevance } from '@upright/common/model/company-client-data/types';
import { ImpactCategoryId } from '@upright/common/model/input/impacts/impact-categories';
import { Valence } from '@upright/common/model/types';
import { compact, Dictionary, groupBy, mapValues } from 'lodash';
import { ClientDatum } from './types';

/**
 * OA: This function chooses the impact categories that should be present when drawing boxes. It picks impact categories that
 * have client data, and sorts them by highest relevance/magnitude.
 * @returns an array of impact categories to show boxes for. The length of the array <= maxBoxes.
 *
 * NOTE: In some cases return-array.length < maxBoxes && return-array.length < data.length, or in other words, the data contains less impact categories than
 * there will be boxes. In this case, the code after this function will decide on how to split the data of a single impact category into multiple boxes.
 */
export function getShownImpactCategories<T extends ClientDatum>(data: T[], valence: Valence, maxBoxes: number) {
  const valenceFilteredItems = data.filter(d => d.valence === valence);

  const itemsByImpactCategory = groupBy(valenceFilteredItems, d => d.impactCategoryId);
  const impactCategoriesPresent = Object.keys(itemsByImpactCategory) as ImpactCategoryId[];

  const { highestRelevanceByImpactCategory, highestMagnitudeByImpactCategory } =
    pickDataWithHighestValue<T>(itemsByImpactCategory);

  const sortedCategories = impactCategoriesPresent.sort((a, b) => {
    // Prioritize impact categories that have high relevance data present.
    const aRelevance = highestRelevanceByImpactCategory[a];
    const bRelevance = highestRelevanceByImpactCategory[b];
    if (aRelevance === bRelevance) {
      // If tied, prioritize impact categories that have high magnitude data present.
      const aMagnitude = highestMagnitudeByImpactCategory[a] || 0;
      const bMagnitude = highestMagnitudeByImpactCategory[b] || 0;
      // If still tied (for example targets don't have magnitudes), keep whatever order the data was in.
      if (aMagnitude === bMagnitude) return 0;
      return aMagnitude > bMagnitude ? -1 : 1;
    }
    return aRelevance > bRelevance ? -1 : 1;
  });
  return sortedCategories.slice(0, maxBoxes);
}

function pickDataWithHighestValue<T extends ClientDatum>(itemsByImpactCategory: Dictionary<T[]>) {
  const highestRelevanceByImpactCategory = mapValues(
    mapValues(itemsByImpactCategory, items => items.map(i => toNumericRelevance(i.relevance))),
    relevances => Math.max(...relevances),
  );

  const highestMagnitudeByImpactCategory = mapValues(
    mapValues(itemsByImpactCategory, items => items.map(i => i.magnitude)),
    magnitudes => {
      const magnitudesPresent = compact(magnitudes);
      if (!magnitudesPresent.length) {
        return undefined;
      }
      return Math.max(...magnitudesPresent);
    },
  );
  return { highestRelevanceByImpactCategory, highestMagnitudeByImpactCategory };
}
